import type { StaticImageData } from "next/image";
import Image from "next/image";
import { useCallback, useMemo } from "react";

import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH } from "~/utils/card";

export const FannedDeckCardPreview = ({
  position,
  src,
}: {
  position: number;
  src: string | StaticImageData;
}) => {
  const cardWidth = useMemo(() => 80, []);

  const delta = useMemo(
    () => `calc(
    (${3} * ${cardWidth}px) - 100%
  )`,
    [cardWidth],
  );

  const offset = useMemo(() => `calc(${delta} / (${3} - 1))`, [delta]);

  const left = useMemo(
    () => `calc(${position} * (${cardWidth}px - ${offset}))`,
    [position, cardWidth, offset],
  );

  const getRotation = useCallback((totalCards: number, cardIndex: number) => {
    const baseRotation = 10; // You can adjust this value to get the desired effect.
    const half = Math.floor(totalCards / 2);

    // If the total number of cards is even, we need to adjust the pivot slightly.
    const isEven = totalCards % 2 === 0;
    const pivot = isEven ? half - 0.5 : half;

    const distanceFromPivot = cardIndex - pivot;
    const rotation = baseRotation * distanceFromPivot;

    return rotation;
  }, []);

  const getTopOffset = useCallback((totalCards: number, cardIndex: number) => {
    const half = Math.floor(totalCards / 2);
    const isEven = totalCards % 2 === 0;
    const pivot = isEven ? half - 0.5 : half;

    const distanceFromPivot = cardIndex - pivot;
    const edgeOffset = cardIndex === 0 || cardIndex === totalCards - 1 ? totalCards * 3 : 0;
    const total = Math.abs(distanceFromPivot);
    return total + edgeOffset;
  }, []);

  const rotation = useMemo(() => {
    const r = getRotation(3, position);
    return Math.sign(r) === 1 ? Math.min(r, 50) : Math.max(r, -50);
  }, [getRotation, position]);

  return (
    <div
      style={{
        left: left,
        width: cardWidth,
        transform: `perspective(1000px) rotateZ(${rotation}deg)`,
        zIndex: `calc(3 - ${position})`,
        paddingTop: getTopOffset(3, position),
      }}
      className={position === 0 ? "relative" : "absolute"}
    >
      <Image
        className="rounded-[8px] shadow-[0_0_12px_rgba(0,0,0,1)]"
        height={DEFAULT_CARD_HEIGHT}
        width={DEFAULT_CARD_WIDTH}
        alt={""}
        src={src}
      />
    </div>
  );
};
